/** R3 of CONTACTS_REFACTOR — single-contact detail with cross-campaign
 * Memberships + outcomes timeline + persistent flags (do_not_call,
 * is_invalid, GDPR erasure). Extended in P5 follow-up with multi-
 * address management + Layer C custom fields + is_business toggle. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { formatDateTime } from "@/lib/format";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { PermissionGate } from "@/components/PermissionGate";
import { ApiPaths, api } from "@/lib/api";
import { displayPhone } from "@/lib/phone";
import {
  ContactAddresses,
  ContactedByBadge,
  LastContactedLine,
  MembershipFieldsCard,
  ScheduleLeadModal,
  resolveFieldSchema,
} from "@/modules/campaigns";
import { useHasPermission } from "@/lib/permissions";
import type { CampaignMembership, Contact } from "@/lib/types";


export default function ContactDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const canManageMembers = useHasPermission("campaign.members.manage");

  const contact = useQuery({
    queryKey: ["contact", id],
    queryFn: async () => {
      const res = await api.get<Contact>(ApiPaths.contact(id!));
      return res.data;
    },
    enabled: !!id,
  });

  // The membership the operator is rescheduling, if any. Held here
  // rather than per row so only one modal can ever be open.
  const [rescheduling, setRescheduling] = useState<CampaignMembership | null>(
    null,
  );

  const memberships = useQuery({
    queryKey: ["contact-memberships", id],
    queryFn: async () => {
      const res = await api.get<CampaignMembership[]>(
        ApiPaths.contactMemberships(id!),
      );
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!id,
  });

  const patch = useMutation({
    mutationFn: async (payload: Partial<Contact>) => {
      const res = await api.patch<Contact>(ApiPaths.contact(id!), payload);
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["contact", id], data);
    },
  });

  const erase = useMutation({
    mutationFn: async () => {
      await api.delete(ApiPaths.contact(id!));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contact", id] });
    },
  });

  if (contact.isLoading) {
    return (
      <AppLayout title={t("contacts.detailTitle")}>
        <p className="card text-sm text-nb-text-3">{t("common.loading")}</p>
      </AppLayout>
    );
  }
  if (!contact.data) {
    // A 404 really means "this beneficiary doesn't exist"; any other
    // failure (network blip, 500) is transient — telling the operator
    // the record doesn't exist in that case is actively misleading,
    // not just unhelpful.
    const is404 =
      axios.isAxiosError(contact.error) && contact.error.response?.status === 404;
    return (
      <AppLayout title={t("contacts.detailTitle")}>
        <p className="card text-sm text-cvp-danger">
          {is404
            ? t("contacts.notFound")
            : t("contacts.detailLoadError", "Could not load this beneficiary.")}
        </p>
      </AppLayout>
    );
  }
  const c = contact.data;

  return (
    <AppLayout
      eyebrow={t("contacts.eyebrow")}
      title={`${c.first_name} ${c.last_name}`}
      headerAction={
        <PermissionGate perm="campaign.members.manage">
          <Link to={`/contacts/${c.id}/edit`} className="btn-primary text-sm">
            {t("contacts.editButton", "Edit")}
          </Link>
        </PermissionGate>
      }
    >
      <BackLink
        fallback="/contacts"
        className="text-sm text-nb-text-2 hover:underline"
      >
        ← {t("common.back")}
      </BackLink>

      {/* Identity card — read-only; edit via the Edit button → edit page. */}
      <IdentitySection contact={c} />

      {/* Legal-entity + business_type (read-only). */}
      <BusinessSection contact={c} />

      {/* Layer C custom fields (DNI / CIF / SIRET / org-defined) — read-only. */}
      <ContactCustomFieldsSection contact={c} />

      {/* Multi-address card. */}
      <PermissionGate perm="campaign.read">
        <ContactAddresses contactId={c.id} isBusiness={c.is_business} />
      </PermissionGate>

      {/* Flags */}
      <PermissionGate perm="campaign.members.manage">
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("contacts.flagsHeading")}
          </h2>
          <p className="text-xs text-nb-text-3">{t("contacts.flagsHint")}</p>
          <div className="flex flex-wrap gap-3 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={c.do_not_call}
                disabled={patch.isPending}
                onChange={(e) =>
                  patch.mutate({ do_not_call: e.target.checked })
                }
              />
              {t("contacts.flagDoNotCallLabel")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={c.do_not_email}
                disabled={patch.isPending}
                onChange={(e) =>
                  patch.mutate({ do_not_email: e.target.checked })
                }
              />
              {t("contacts.flagDoNotEmailLabel")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={c.is_invalid}
                disabled={patch.isPending}
                onChange={(e) =>
                  patch.mutate({ is_invalid: e.target.checked })
                }
              />
              {t("contacts.flagInvalidLabel")}
            </label>
          </div>
        </section>
      </PermissionGate>

      {/* Memberships table */}
      <section className="card space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("contacts.membershipsHeading")}
        </h2>
        {memberships.isLoading && (
          <p className="text-xs text-nb-text-3">{t("common.loading")}</p>
        )}
        {memberships.isError && (
          <p className="text-xs text-cvp-danger">
            {t("contacts.membershipsLoadError", "Could not load campaign history.")}
          </p>
        )}
        {memberships.data && memberships.data.length === 0 && (
          <p className="text-xs text-nb-text-3">
            {t("contacts.membershipsEmpty")}
          </p>
        )}
        {memberships.data && memberships.data.length > 0 && (
          <ul className="divide-y divide-nb-border">
            {memberships.data.map((m) => (
              <li key={m.id} className="py-2 text-sm">
                <Link
                  to={`/campaigns/${m.campaign}`}
                  className="font-medium text-nb-text hover:text-nb-mint hover:underline"
                >
                  {m.campaign_name ||
                    `${t("contacts.campaignPrefix")} ${m.campaign.slice(0, 8)}`}
                </Link>
                <span className="ml-2 pill text-xs">
                  {t(`leads.status.${m.status}`, m.status)}
                </span>
                {/* Who reached them in THIS campaign. Per-membership, not
                    per-contact: the same person can be on the wave in one
                    campaign and hand-booked in another, and a single
                    badge on the page header would have to pick one. */}
                <span className="ml-2">
                  <ContactedByBadge
                    membership={m}
                    testId={`membership-contacted-by-${m.id}`}
                  />
                </span>
                {m.outcome_reason && (
                  <span className="ml-2 text-xs text-nb-text-3">
                    · {m.outcome_reason}
                  </span>
                )}
                {/* Reported 2026-09-25. This page is the top-level
                    Beneficiaries surface, and it showed which campaigns
                    someone is in without ever saying WHEN they are being
                    seen, or offering a way to move it. Same chip and the
                    same modal as both drawers — one reschedule
                    implementation, three places that reach it. */}
                {m.next_scheduled_for && (
                  <span
                    className="ml-2 inline-flex items-center rounded-full border border-nb-mint/40 bg-nb-mint/10 px-2 py-0.5 text-[11px] font-medium text-nb-mint"
                    data-testid="membership-scheduled-chip"
                  >
                    {t("leads.col.scheduled")}:{" "}
                    {formatDateTime(m.next_scheduled_for)}
                  </span>
                )}
                {!m.is_archived && (
                  <PermissionGate perm="campaign.members.manage">
                    <button
                      type="button"
                      className="btn-ghost ml-2 text-xs"
                      onClick={() => setRescheduling(m)}
                      data-testid="membership-reschedule"
                    >
                      {m.next_scheduled_for
                        ? t("leads.rescheduleButton")
                        : t("leads.scheduleButton")}
                    </button>
                  </PermissionGate>
                )}
                <LastContactedLine
                  membership={m}
                  testId={`membership-contacted-detail-${m.id}`}
                  className="mt-1 text-xs text-nb-text-2"
                />
                {/* Per-campaign (membership) custom fields — this beneficiary's
                    data for THIS campaign; editable in place when allowed. */}
                <MembershipFieldsCard
                  campaign={m.campaign}
                  membershipId={m.id}
                  values={m.custom_fields_jsonb}
                  canEdit={canManageMembers}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* GDPR erasure */}
      <PermissionGate perm="campaign.members.manage">
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("contacts.gdprHeading")}
          </h2>
          {c.gdpr_erasure_requested_at ? (
            <p className="text-sm text-cvp-warn">
              {t("contacts.gdprAlreadyRequested", {
                when: formatDateTime(c.gdpr_erasure_requested_at),
              })}
            </p>
          ) : (
            <>
              <p className="text-xs text-nb-text-3">{t("contacts.gdprHint")}</p>
              <button
                type="button"
                className="btn-ghost text-sm text-cvp-danger"
                onClick={() => {
                  if (window.confirm(t("contacts.gdprConfirm"))) {
                    erase.mutate();
                  }
                }}
                disabled={erase.isPending}
              >
                {t("contacts.gdprButton")}
              </button>
            </>
          )}
        </section>
      </PermissionGate>
      {rescheduling && (
        <ScheduleLeadModal
          lead={rescheduling}
          campaignId={rescheduling.campaign}
          onClose={() => {
            setRescheduling(null);
            void memberships.refetch();
          }}
        />
      )}
    </AppLayout>
  );
}


/** A single read-only label/value pair inside a two-column <dl> grid. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-nb-text-3">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** Core beneficiary identity — READ-ONLY display. Editing happens on the
 * dedicated edit page (Edit button in the header), so the detail view stays a
 * clean read surface. */
function IdentitySection({ contact }: { contact: Contact }) {
  const { t } = useTranslation();
  const name =
    `${contact.first_name} ${contact.last_name}`.trim() || "—";
  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("contacts.identityHeading")}
      </h2>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <Row label={t("contacts.col.name")}>{name}</Row>
        {contact.email && (
          <Row label={t("contacts.col.email")}>{contact.email}</Row>
        )}
        {contact.phone && (
          <Row label={t("contacts.col.phone")}>
            <span className="font-mono">{displayPhone(contact.phone)}</span>
          </Row>
        )}
        {/* Address lives in the ADDRESSES section below — not duplicated here. */}
        {contact.external_ref && (
          <Row label={t("contacts.col.externalRef")}>
            <span className="font-mono text-xs">{contact.external_ref}</span>
          </Row>
        )}
      </dl>
    </section>
  );
}


function BusinessSection({ contact }: { contact: Contact }) {
  const { t } = useTranslation();
  // Individuals: nothing to show — keep the detail view uncluttered.
  if (!contact.is_business) return null;
  const hasContact =
    contact.company_contact_name ||
    contact.company_contact_email ||
    contact.company_contact_phone;
  return (
    <section className="card space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("contacts.businessHeading", "Legal entity")}
      </h2>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        {contact.business_type && (
          <Row label={t("contacts.businessType", "Business type")}>
            {t(
              `contacts.businessType_${contact.business_type}`,
              contact.business_type,
            )}
          </Row>
        )}
        {contact.legal_name && (
          <Row label={t("party.legalName", "Legal name")}>
            {contact.legal_name}
          </Row>
        )}
        {contact.registration_number && (
          <Row label={t("party.registrationNumber", "Registration number")}>
            <span className="font-mono">{contact.registration_number}</span>
          </Row>
        )}
        {contact.vat_id && (
          <Row label={t("party.vatId", "VAT ID")}>
            <span className="font-mono">{contact.vat_id}</span>
          </Row>
        )}
      </dl>
      {hasContact && (
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-nb-text-3">
            {t("contacts.companyContactHeading", "Company contact")}
          </p>
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
            {contact.company_contact_name && (
              <Row label={t("contacts.companyContactName", "Contact name")}>
                {contact.company_contact_name}
              </Row>
            )}
            {contact.company_contact_email && (
              <Row label={t("contacts.col.email")}>
                {contact.company_contact_email}
              </Row>
            )}
            {contact.company_contact_phone && (
              <Row label={t("contacts.col.phone")}>
                <span className="font-mono">
                  {displayPhone(contact.company_contact_phone)}
                </span>
              </Row>
            )}
          </dl>
        </div>
      )}
    </section>
  );
}


function ContactCustomFieldsSection({ contact }: { contact: Contact }) {
  const { t } = useTranslation();
  const country = (contact.country || "").toUpperCase();
  const schema = useQuery({
    queryKey: ["fields-schema", "contact", country],
    queryFn: () => resolveFieldSchema({ applies_to: "contact", country }),
    enabled: true,
  });

  const rows = useMemo(() => schema.data ?? [], [schema.data]);
  // Only render fields that actually have a value — read-only view.
  const filled = rows.filter(
    (fd) => (contact.custom_fields_jsonb?.[fd.key] ?? "") !== "",
  );
  if (schema.isLoading || filled.length === 0) {
    return null;
  }

  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("contacts.customFieldsHeading", "Custom fields")}
      </h2>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        {filled.map((fd) => (
          <Fragment key={fd.id}>
            <dt className="text-nb-text-3">
              {fd.label}
              {fd.is_pii && (
                <span className="ml-1 text-[10px] uppercase text-cvp-warn">
                  PII
                </span>
              )}
            </dt>
            <dd>{String(contact.custom_fields_jsonb?.[fd.key] ?? "")}</dd>
          </Fragment>
        ))}
      </dl>
    </section>
  );
}


